// Three-way merge of block lists (a note's top-level blocks as HTML strings) — pure.
// base = what both sides last agreed on, mine = what's being typed here, theirs = what was saved elsewhere.
// Each side is lined up against base block by block, where a changed block still counts as that block ("alpha" →
// "alpha ✅"), so edits to neighbouring blocks never collide. Then, per base block: an edit on one side wins over no
// edit; the same edit lands once; different edits keep mine, then theirs (nothing is lost); a delete wins over no edit
// but loses to an edit. New blocks go in where they were added (mine first). `map[i]` = where mine's block i ended up.

const text = (h) => String(h || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

/** Is `b` an edit of `a` (rather than a different block)? Shares most of its start + end, or one contains the other. */
function similar(a, b) {
  const x = text(a); const y = text(b);
  if (!x || !y) return x === y && a.slice(0, 4) === b.slice(0, 4);
  if (x.includes(y) || y.includes(x)) return true;
  let p = 0; while (p < x.length && p < y.length && x[p] === y[p]) p++;
  let s = 0; while (s < x.length - p && s < y.length - p && x[x.length - 1 - s] === y[y.length - 1 - s]) s++;
  return (p + s) / Math.max(x.length, y.length) >= 0.5;
}

/**
 * Line a side up against base: for each base block { i: side index or -1, mod: changed? }, and the side's new blocks
 * as ins[k] = side indices inserted before base block k (k = base.length → at the end).
 */
function align(base, side) {
  const n = base.length; const m = side.length;
  const score = Array.from({ length: n + 1 }, () => new Int32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      const pair = base[i] === side[j] ? 3 : similar(base[i], side[j]) ? 2 : -1;
      score[i][j] = Math.max(score[i + 1][j], score[i][j + 1], pair > 0 ? score[i + 1][j + 1] + pair : -1);
    }
  }
  const at = new Array(n).fill(null).map(() => ({ i: -1, mod: false }));
  const ins = Array.from({ length: n + 1 }, () => []);
  let i = 0; let j = 0;
  while (i < n && j < m) {
    const pair = base[i] === side[j] ? 3 : similar(base[i], side[j]) ? 2 : -1;
    if (pair > 0 && score[i][j] === score[i + 1][j + 1] + pair) { at[i] = { i: j, mod: pair !== 3 }; i++; j++; }
    else if (score[i][j] === score[i + 1][j]) i++;
    else { ins[i].push(j); j++; }
  }
  while (j < m) ins[n].push(j++);
  return { at, ins };
}

// opts.onConflict: 'both' (default — mine, then theirs, inline) | 'mine' (keep mine in that block; the caller files
// theirs whole as a conflict copy — notesync.js). `conflictAt` = indexes in `blocks` of the blocks both sides edited.
export function merge3(base = [], mine = [], theirs = [], { onConflict = 'both' } = {}) {
  const M = align(base, mine); const T = align(base, theirs);
  const blocks = []; const map = new Array(mine.length).fill(null); let conflicts = 0; const conflictAt = [];
  const putMine = (j) => { map[j] = blocks.length; blocks.push(mine[j]); };
  const inserts = (k) => {
    const mi = M.ins[k]; const ti = T.ins[k];
    mi.forEach(putMine);
    for (const j of ti) if (!mi.some((x) => mine[x] === theirs[j])) blocks.push(theirs[j]);
  };
  for (let k = 0; k < base.length; k++) {
    inserts(k);
    const a = M.at[k]; const b = T.at[k];
    const mineGone = a.i < 0; const theirsGone = b.i < 0;
    if (mineGone && theirsGone) continue;
    if (mineGone) { if (b.mod) blocks.push(theirs[b.i]); continue; }       // I deleted; keep theirs only if they edited it
    if (theirsGone) { if (a.mod) putMine(a.i); continue; }                   // they deleted; keep mine only if I edited it
    if (!a.mod && b.mod) { map[a.i] = blocks.length; blocks.push(theirs[b.i]); continue; }   // only they edited
    if (a.mod && b.mod && mine[a.i] !== theirs[b.i]) { conflicts++; conflictAt.push(blocks.length); putMine(a.i); if (onConflict !== 'mine') blocks.push(theirs[b.i]); continue; }
    putMine(a.i);                                                            // only I edited, both the same, or neither
  }
  inserts(base.length);
  // Blocks of mine that were dropped (deleted on their side, untouched here) → point at the nearest block kept after.
  for (let j = mine.length - 1, next = blocks.length - 1; j >= 0; j--) { if (map[j] == null) map[j] = Math.max(0, next); else next = map[j]; }
  return { blocks, map, conflicts, conflictAt };
}
